<template>
  <div v-if="text && isVisible" class="typing-indicator">
    <div class="typing-content">
      <div class="typing-dots">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <span class="typing-text">{{ text }}</span>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'

export default {
  name: 'TypingIndicator',
  props: {
    text: {
      type: String,
      default: ''
    },
    delay: {
      type: Number,
      default: 500 // ms avant d'afficher l'indicateur
    },
    minDisplayTime: {
      type: Number,
      default: 1000 // temps minimum d'affichage
    }
  },
  setup(props) {
    const isVisible = ref(false)
    const showTimeout = ref(null)
    const hideTimeout = ref(null)

    const show = () => {
      // Annuler les timeouts précédents
      if (showTimeout.value) clearTimeout(showTimeout.value)
      if (hideTimeout.value) clearTimeout(hideTimeout.value)

      // Afficher après le délai
      showTimeout.value = setTimeout(() => {
        isVisible.value = true
      }, props.delay)
    }

    const hide = () => {
      // Annuler le timeout d'affichage
      if (showTimeout.value) clearTimeout(showTimeout.value)

      if (isVisible.value) {
        // Attendre le temps minimum d'affichage
        hideTimeout.value = setTimeout(() => {
          isVisible.value = false
        }, props.minDisplayTime)
      } else {
        // Annuler l'affichage prévu
        if (showTimeout.value) {
          clearTimeout(showTimeout.value)
          showTimeout.value = null
        }
      }
    }

    onMounted(() => {
      if (props.text) {
        show()
      }
    })

    onUnmounted(() => {
      if (showTimeout.value) clearTimeout(showTimeout.value)
      if (hideTimeout.value) clearTimeout(hideTimeout.value)
    })

    return {
      isVisible,
      show,
      hide
    }
  },
  watch: {
    text: {
      immediate: true,
      handler(newText) {
        if (newText) {
          this.show()
        } else {
          this.hide()
        }
      }
    }
  }
}
</script>

<style scoped>
.typing-indicator {
  display: flex;
  justify-content: flex-start;
  padding: 0.5rem 1rem;
  animation: fadeIn 0.3s ease-in;
}

.typing-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f3f4f6;
  border-radius: 1rem;
  padding: 0.5rem 0.75rem;
  max-width: 70%;
}

.typing-dots {
  display: flex;
  gap: 3px;
  align-items: center;
}

.typing-dots span {
  width: 6px;
  height: 6px;
  background: #6b7280;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

.typing-text {
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 640px) {
  .typing-content {
    padding: 0.4rem 0.6rem;
    max-width: 80%;
  }

  .typing-text {
    font-size: 0.8rem;
  }

  .typing-dots span {
    width: 5px;
    height: 5px;
  }
}
</style>